import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Button, TextField, Typography, Paper, Divider
} from "@mui/material";
import { instance } from "./config";
import { LoaderBoxCircular } from "./LoaderBox";

const CUATRIMESTRES_ORDENADOS = ["Primer", "Segundo", "Tercero"];

const MAPA_CUATRIMESTRES = {
  1: "Primer", 2: "Primer", 3: "Primer", 4: "Primer",
  5: "Segundo", 6: "Segundo", 7: "Segundo", 8: "Segundo",
  9: "Tercero", 10: "Tercero", 11: "Tercero", 12: "Tercero",
};

function UPSE8DetalleProducto({
  open,
  onClose,
  onSubmit,
  producto,
  esSemillas
}) {
  const [disminucion, setDisminucion] = useState(0);
  const [incremento, setIncremento] = useState(0);
  const [justificacion, setJustificacion] = useState("");
  const [meses, setMeses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mensajeError, setMensajeError] = useState("");

  useEffect(() => {
    if (!open || !producto) return;

    if (producto.reprogramacion) {
      setDisminucion(producto.reprogramacion.disminucion);
      setIncremento(producto.reprogramacion.incremento);
      setJustificacion(producto.reprogramacion.justificacion);
      setMeses(producto.reprogramacion.meses);
      return;
    }

    setIsLoading(true);
    const endpoint = esSemillas
      ? `/upse8/metaMensualVigenteCultivar/${producto.id}`
      : `/upse8/metaMensualVigente/${producto.id}`;
    instance.get(endpoint)
      .then((res) => {
        setMeses(res.data);
        setDisminucion(0);
        setIncremento(0);
        setJustificacion("");
      })
      .catch(() => { })
      .finally(() => setIsLoading(false));
  }, [open, producto, esSemillas]);

  const metaModificadaFinal = useMemo(() => {
    return Number(producto?.metaInicial || 0) - Number(disminucion || 0) + Number(incremento || 0);
  }, [producto, disminucion, incremento]);

  const totalMeses = useMemo(() => {
    return meses.reduce((acc, m) => acc + Number(m.cantidad || 0), 0);
  }, [meses]);

  const totalesPorCuatrimestre = useMemo(() => {
    const acumulador = { Primer: 0, Segundo: 0, Tercero: 0 };
    meses.forEach(({ idMes, cantidad }) => {
      const cuatri = MAPA_CUATRIMESTRES[idMes];
      if (cuatri) acumulador[cuatri] += Number(cantidad || 0);
    });
    return acumulador;
  }, [meses]);

  const editarCantidad = (idMes, valor) => {
    setMeses((prev) => prev.map((m) => (
      m.idMes === idMes ? { ...m, cantidad: valor === "" ? "" : parseFloat(valor) } : m
    )));
  };

  const cancelar = () => {
    setMensajeError("");
    onClose();
  };

  const registrar = () => {
    if (!justificacion.trim()) {
      setMensajeError("* Debe ingresar la justificación del incremento o disminución");
      return;
    }
    if (Number(totalMeses) !== Number(metaModificadaFinal)) {
      setMensajeError(`* La suma de los 12 meses (${totalMeses}) debe coincidir con la meta modificada final (${metaModificadaFinal})`);
      return;
    }
    setMensajeError("");

    const payload = {
      id: producto.id,
      metaInicial: Number(producto.metaInicial),
      disminucion: Number(disminucion || 0),
      incremento: Number(incremento || 0),
      metaModificadaFinal: Number(metaModificadaFinal),
      justificacion,
      meses: meses.map((m) => ({ idMes: m.idMes, mes: m.mes, cantidad: Number(m.cantidad || 0) }))
    };

    onSubmit(payload);
    onClose();
  };

  return (
    <Dialog open={open} onClose={cancelar} maxWidth="md" fullWidth>
      <DialogTitle>Reprogramación de {producto?.detalle}</DialogTitle>
      <DialogContent dividers>
        <LoaderBoxCircular isLoading={isLoading}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {producto?.departamento} · {producto?.municipio} · {producto?.localidad}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                disabled fullWidth label="Meta inicial" type="number"
                value={producto?.metaInicial ?? 0}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth label="Disminución" type="number"
                value={disminucion}
                onChange={(e) => setDisminucion(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth label="Incremento" type="number"
                value={incremento}
                onChange={(e) => setIncremento(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                disabled fullWidth label="Meta modificada final" type="number"
                value={metaModificadaFinal}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={2}
                label="Justificación del incremento o disminución"
                value={justificacion}
                onChange={(e) => setJustificacion(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">Nueva distribución mensual</Typography>
            </Grid>

            {CUATRIMESTRES_ORDENADOS.map((cuatri) => (
              <Grid item xs={12} key={cuatri}>
                <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>{cuatri} cuatrimestre</Typography>
                <Grid container spacing={1}>
                  {meses.filter((m) => MAPA_CUATRIMESTRES[m.idMes] === cuatri).map((m) => (
                    <Grid item xs={6} sm={3} key={m.idMes}>
                      <TextField
                        fullWidth
                        size="small"
                        label={m.mes}
                        type="number"
                        value={m.cantidad}
                        onChange={(e) => editarCantidad(m.idMes, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={3}>
                    <Paper elevation={1} sx={{ p: 1, textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight="bold">Subtotal</Typography>
                      <Typography variant="body1">{totalesPorCuatrimestre[cuatri]}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>Total: {totalMeses}</Typography>
            </Grid>
          </Grid>
        </LoaderBoxCircular>
      </DialogContent>

      <DialogActions>
        <Typography color="error" marginRight={2}>{mensajeError}</Typography>
        <Button onClick={cancelar}>Cancelar</Button>
        <Button onClick={registrar} variant="contained" color="primary">Registrar</Button>
      </DialogActions>
    </Dialog>
  );
}

export { UPSE8DetalleProducto };
